/**
 * TB Mukt Bharat - Notifications Page
 * Real data from /api/notifications. Every click is functional:
 * row click / check = mark as read, trash = delete, "Mark all read",
 * "Clear read" (delete all read), and filter tabs filter the list.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bell, Heart, MessageCircle, UserPlus, AlertCircle, Check, Trash2,
  AtSign, Mail, Flag, Loader2,
} from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovEmptyState, GovButton } from '@/components/layout/GovLayout'
import { notificationService, type AppNotification } from '@/services/notificationService'

// ================================================================================
// HELPERS
// ================================================================================

/** Relative time from an ISO/LocalDateTime string. */
function timeAgo(iso: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diff = Math.max(0, Date.now() - then)
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} min ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr > 1 ? 's' : ''} ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day} day${day > 1 ? 's' : ''} ago`
  const wk = Math.floor(day / 7)
  if (wk < 5) return `${wk} week${wk > 1 ? 's' : ''} ago`
  return new Date(then).toLocaleDateString()
}

function isToday(iso: string): boolean {
  const d = new Date(iso)
  const n = new Date()
  return (
    d.getDate() === n.getDate() &&
    d.getMonth() === n.getMonth() &&
    d.getFullYear() === n.getFullYear()
  )
}

// Backend NotificationType -> icon + color
const ICON_MAP: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
  POST_LIKE: { icon: Heart, bg: 'bg-red-50', color: 'text-red-500' },
  LIKE: { icon: Heart, bg: 'bg-red-50', color: 'text-red-500' },
  COMMENT: { icon: MessageCircle, bg: 'bg-blue-50', color: 'text-blue-500' },
  FOLLOW: { icon: UserPlus, bg: 'bg-green-50', color: 'text-green-500' },
  FRIEND_REQUEST: { icon: UserPlus, bg: 'bg-green-50', color: 'text-green-500' },
  MENTION: { icon: AtSign, bg: 'bg-purple-50', color: 'text-purple-500' },
  MESSAGE: { icon: Mail, bg: 'bg-blue-50', color: 'text-blue-500' },
  SYSTEM: { icon: AlertCircle, bg: 'bg-amber-50', color: 'text-amber-500' },
  REPORT: { icon: Flag, bg: 'bg-red-50', color: 'text-red-500' },
}

// Filter tab -> set of notification types it includes
const FILTER_TABS: { label: string; types: string[] | null }[] = [
  { label: 'All', types: null },
  { label: 'Likes', types: ['POST_LIKE', 'LIKE'] },
  { label: 'Comments', types: ['COMMENT'] },
  { label: 'Follows', types: ['FOLLOW', 'FRIEND_REQUEST'] },
  { label: 'Mentions', types: ['MENTION'] },
  { label: 'System', types: ['SYSTEM', 'REPORT'] },
]

// ================================================================================
// NOTIFICATION ITEM
// ================================================================================
const NotificationItem = React.memo(
  ({
    notification,
    onMarkRead,
    onDelete,
  }: {
    notification: AppNotification
    onMarkRead: (id: number) => void
    onDelete: (id: number) => void
  }) => {
    const { icon: Icon, bg, color } = ICON_MAP[notification.type] || {
      icon: Bell,
      bg: 'bg-gray-50',
      color: 'text-gray-500',
    }

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => !notification.isRead && onMarkRead(notification.id)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !notification.isRead) {
            e.preventDefault()
            onMarkRead(notification.id)
          }
        }}
        className={`flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer ${
          notification.isRead ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/50 hover:bg-blue-50'
        }`}
        title={notification.isRead ? notification.title : `${notification.title} — click to mark as read`}
      >
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${notification.isRead ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
            {notification.message || notification.title}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{timeAgo(notification.createdAt)}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {!notification.isRead && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onMarkRead(notification.id)
              }}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              title="Mark as read"
            >
              <Check className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete(notification.id)
            }}
            className="p-1.5 hover:bg-red-50 rounded-full transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
          </button>
        </div>
      </div>
    )
  },
)
NotificationItem.displayName = 'NotificationItem'

// ================================================================================
// NOTIFICATIONS PAGE
// ================================================================================
const NotificationsPage = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<string>('All')
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const flash = useCallback((type: 'success' | 'error', text: string) => {
    setMsg({ type, text })
    window.setTimeout(() => setMsg(null), 4000)
  }, [])

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await notificationService.getNotifications(0, 50)
      setNotifications(res.content || [])
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications],
  )
  const readCount = useMemo(() => notifications.filter((n) => n.isRead).length, [notifications])

  const handleMarkRead = useCallback(
    async (id: number) => {
      // Optimistic
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)))
      try {
        await notificationService.markAsRead(id)
      } catch (e: any) {
        flash('error', 'Could not mark as read')
        // Revert to server truth
        fetchNotifications()
      }
    },
    [fetchNotifications, flash],
  )

  const handleDelete = useCallback(
    async (id: number) => {
      const prev = notifications
      setNotifications((cur) => cur.filter((n) => n.id !== id))
      try {
        await notificationService.deleteNotification(id)
        flash('success', 'Notification deleted')
      } catch (e: any) {
        setNotifications(prev) // revert
        flash('error', 'Could not delete notification')
      }
    },
    [notifications, flash],
  )

  const handleMarkAllRead = useCallback(async () => {
    if (unreadCount === 0) return
    const prev = notifications
    setNotifications((cur) => cur.map((n) => ({ ...n, isRead: true })))
    try {
      await notificationService.markAllAsRead()
      flash('success', 'All notifications marked as read')
    } catch (e: any) {
      setNotifications(prev) // revert
      flash('error', 'Could not mark all as read')
    }
  }, [notifications, unreadCount, flash])

  const handleClearRead = useCallback(async () => {
    if (readCount === 0) return
    const prev = notifications
    setNotifications((cur) => cur.filter((n) => !n.isRead))
    try {
      await notificationService.deleteAllRead()
      flash('success', 'Read notifications cleared')
    } catch (e: any) {
      setNotifications(prev) // revert
      flash('error', 'Could not clear read notifications')
    }
  }, [notifications, readCount, flash])

  // Apply filter tab
  const filtered = useMemo(() => {
    const tab = FILTER_TABS.find((t) => t.label === activeFilter)
    if (!tab || tab.types === null) return notifications
    return notifications.filter((n) => tab.types!.includes(n.type))
  }, [notifications, activeFilter])

  // Group by Today / Earlier
  const grouped = useMemo(() => {
    const today = filtered.filter((n) => isToday(n.createdAt))
    const earlier = filtered.filter((n) => !isToday(n.createdAt))
    return { today, earlier }
  }, [filtered])

  return (
    <GovLayout>
      <GovPageTitle
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
        icon={Bell}
      />

      {/* Banner */}
      {msg && (
        <div
          className={`mb-3 px-4 py-2 rounded-lg text-sm ${
            msg.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* Actions Bar */}
      {!loading && notifications.length > 0 && (
        <div className="flex justify-end gap-2 mb-3 flex-wrap">
          {unreadCount > 0 && (
            <GovButton variant="ghost" size="sm" onClick={handleMarkAllRead}>
              <Check className="w-4 h-4 mr-1" />
              Mark all read
            </GovButton>
          )}
          {readCount > 0 && (
            <GovButton variant="ghost" size="sm" onClick={handleClearRead}>
              <Trash2 className="w-4 h-4 mr-1" />
              Clear read
            </GovButton>
          )}
        </div>
      )}

      {/* Filter Tabs (functional) */}
      {!loading && notifications.length > 0 && (
        <GovCard className="mb-4">
          <div className="flex gap-2 flex-wrap">
            {FILTER_TABS.map((tab) => (
              <GovButton
                key={tab.label}
                variant={activeFilter === tab.label ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setActiveFilter(tab.label)}
              >
                {tab.label}
              </GovButton>
            ))}
          </div>
        </GovCard>
      )}

      {/* Loading */}
      {loading && (
        <GovCard className="mb-4">
          <div className="flex items-center justify-center py-10 text-gray-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Loading notifications…
          </div>
        </GovCard>
      )}

      {/* Error */}
      {error && !loading && (
        <GovCard className="mb-4">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
            <p className="text-sm text-gray-600 mb-3">{error}</p>
            <GovButton variant="ghost" size="sm" onClick={fetchNotifications}>
              Try again
            </GovButton>
          </div>
        </GovCard>
      )}

      {/* Today */}
      {!loading && !error && grouped.today.length > 0 && (
        <GovCard className="mb-4">
          <h2 className="text-sm font-semibold text-gray-500 mb-3 px-1">Today</h2>
          <div className="space-y-1">
            {grouped.today.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={handleMarkRead}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </GovCard>
      )}

      {/* Earlier */}
      {!loading && !error && grouped.earlier.length > 0 && (
        <GovCard className="mb-4">
          <h2 className="text-sm font-semibold text-gray-500 mb-3 px-1">Earlier</h2>
          <div className="space-y-1">
            {grouped.earlier.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={handleMarkRead}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </GovCard>
      )}

      {/* Empty states */}
      {!loading && !error && notifications.length === 0 && (
        <GovEmptyState
          icon="🔔"
          title="No notifications"
          description="You're all caught up! Check back later for updates."
        />
      )}
      {!loading && !error && notifications.length > 0 && filtered.length === 0 && (
        <GovEmptyState
          icon="🔍"
          title="No matches"
          description={`No ${activeFilter.toLowerCase()} notifications.`}
        />
      )}
    </GovLayout>
  )
}

export default React.memo(NotificationsPage)